import { createStore } from 'zustand/vanilla';
import { persist, createJSONStorage } from 'zustand/middleware';
import { IDEMPOTENCY_STORAGE_KEY, IDEMPOTENCY_TTL_MS } from './config';
import { generateIdempotencyKey } from './generate-key';

export interface IdempotencyEntry {
  key: string;
  createdAt: number;
  scope: string;
  scopeId?: string;
}

export interface IdempotencyState {
  entries: Record<string, IdempotencyEntry>;
  getOrCreate: (scope: string, scopeId?: string) => string;
  reset: (scope: string, scopeId?: string) => string;
  clear: (scope: string, scopeId?: string) => void;
  clearAll: () => void;
  cleanupExpired: () => void;
}

function entryId(scope: string, scopeId?: string): string {
  return `${scope}:${scopeId ?? '_'}`;
}

function isFresh(entry: IdempotencyEntry, now: number): boolean {
  return now - entry.createdAt < IDEMPOTENCY_TTL_MS;
}

export const idempotencyStore = createStore<IdempotencyState>()(
  persist(
    (set, get) => ({
      entries: {},
      getOrCreate: (scope, scopeId) => {
        const id = entryId(scope, scopeId);
        const now = Date.now();
        const existing = get().entries[id];
        if (existing && isFresh(existing, now)) {
          return existing.key;
        }
        const key = generateIdempotencyKey();
        set((state) => ({
          entries: { ...state.entries, [id]: { key, createdAt: now, scope, scopeId } },
        }));
        return key;
      },
      reset: (scope, scopeId) => {
        const id = entryId(scope, scopeId);
        const key = generateIdempotencyKey();
        set((state) => ({
          entries: { ...state.entries, [id]: { key, createdAt: Date.now(), scope, scopeId } },
        }));
        return key;
      },
      clear: (scope, scopeId) => {
        const id = entryId(scope, scopeId);
        set((state) => {
          if (!state.entries[id]) return state;
          const next = { ...state.entries };
          delete next[id];
          return { entries: next };
        });
      },
      clearAll: () => set({ entries: {} }),
      cleanupExpired: () => {
        const now = Date.now();
        set((state) => {
          const next: Record<string, IdempotencyEntry> = {};
          for (const [id, entry] of Object.entries(state.entries)) {
            if (isFresh(entry, now)) next[id] = entry;
          }
          return { entries: next };
        });
      },
    }),
    {
      name: IDEMPOTENCY_STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
